import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { getMyDirectoryListing } from "../services/directoryService";

/**
 * Whether the signed-in user has a directory listing.
 *
 * Drives the navigation swap: someone who already has a listing is offered
 * "My Directory Listing" rather than being invited to join a directory they
 * are already in.
 *
 * The header renders on every page, so the answer is cached per user for the
 * life of the tab and the in-flight request is shared — several components
 * asking at once produce one call, not one each. Signing out or switching
 * accounts changes the key and drops the old answer.
 *
 * Returns false until the answer is known, so the nav never flashes
 * "My Directory Listing" at someone who does not have one. The reverse flash
 * — join, then the swap a moment later — is the safe direction.
 */
const cache = new Map();

function fetchFor(key) {
  if (cache.has(key)) return cache.get(key);

  const token = (() => {
    try {
      return localStorage.getItem("token");
    } catch (e) {
      return null;
    }
  })();

  const promise = getMyDirectoryListing(
    token ? { Authorization: `Bearer ${token}` } : {},
  )
    .then((listing) => !!listing)
    // A failure here must not break the header. No listing is the safe answer:
    // it offers to join, which is never wrong for someone who cannot be asked.
    .catch(() => false);

  cache.set(key, promise);
  return promise;
}

export default function useHasDirectoryListing() {
  const { isAuthenticated, user } = useAuth();
  const key = isAuthenticated ? user?.id || user?.userId || user?.email : null;
  const [hasListing, setHasListing] = useState(false);

  useEffect(() => {
    if (!key) {
      setHasListing(false);
      return undefined;
    }

    let active = true;
    fetchFor(key).then((result) => {
      if (active) setHasListing(result);
    });
    return () => {
      active = false;
    };
  }, [key]);

  return hasListing;
}
